import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { reportsStore } from '../../store/reportsStore';
import { defaultPagination } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from '~/mobx/userStore';
import { IFranchisee, IFranchiseeBranch } from 'src/types/franchises';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { getTranslate } from 'src/utils/helpers';
import CustomLazyPicker from 'src/view/components/Controls/Pickers/CustomLazyPicker/CustomLazyPicker';
import Picker from 'src/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import logger from '~/services/logger';
import './style.scss';
import { getFilterOptionsForPicker } from '~/view/screens/OrdersRatingScreen/helpers';
import { isObjectsEqual } from '~/helpers';

export interface IFranchiseeForSelect {
  id: number;
  name: string;
  data: IBranchForSelect[] | null;
}
export interface IBranchForSelect {
  id: number;
  description: IBranchDescriptionForSelect;
  is_delete?: TBooleanInNumber;
}

interface IBranchDescriptionForSelect {
  branch_name: string;
  language: string;
}

const ReportsFranchiseePicker: React.FC = observer(() => {
  const [paginationForSelect, setPaginationForSelect] = useState<IPagination<IFranchiseeForSelect>>();
  const [savePage, setSavePage] = useState<number>(1);
  const [saveSearchValue, setSaveSearchValue] = useState<string>('');

  const transformToDataForSelect = (branches: IFranchiseeBranch[]) => {
    return branches
      .map((branch: IFranchiseeBranch): IBranchForSelect | null => {
        const userBranch = userStore.allUserBranches.find((ub) => ub.id === branch.id);

        if (!userBranch) {
          return null;
        }

        return {
          id: branch.id,
          description: branch.description,
          is_delete: branch.is_delete === 1 ? 1 : 0,
        };
      })
      .filter((branch): branch is IBranchForSelect => branch !== null);
  };

  const transformToPaginationForSelect = (pagination: IPagination<IFranchisee>): IPagination<IFranchiseeForSelect> => {
    const data: IFranchiseeForSelect[] = pagination.data.map((item): IFranchiseeForSelect => {
      return {
        id: item.id,
        name: item.title,
        data: transformToDataForSelect(item.branches),
      };
    });

    return {
      ...pagination,
      data,
    };
  };

  /**
   * Select franchisee and franchisee branches
   *
   * @param {IPickerOption[]} branchOptions
   */
  const selectFranchisee = (branchOptions: IPickerOption[]) => {
    // Set franchisee to filters
    if (branchOptions.length && !isObjectsEqual(branchOptions, reportsStore.filters.franchisee, ['id'])) {
      reportsStore.setFilters({ franchisee: branchOptions });

      // Set branches to filter
      if (branchOptions[0]?.data) {
        // Transform branches to IPickerOption
        const branches = branchOptions[0]?.data.map((item: IFranchiseeBranch): IPickerOption => {
          return {
            label: getTranslate(item.description.branch_name),
            value: item.id,
            warringText: item.is_delete === 1 ? localeStore.t('branch_was_deleted') : undefined,
          };
        });
        // Select all branches and write them to store
        reportsStore.setFilters({ branches, franchisee_branches: branches });
        reportsStore.selectAllFranchiseeBranches();
      }
    }
  };

  /**
   * Select branches and set to filters
   *
   * @param {IPickerOption[]} branchOptions
   */
  const selectBranches = (branchOptions: IPickerOption[]) => {
    reportsStore.changeBranchSelection(branchOptions, 'franchisee_branches');
  };

  const getFranchisesPagination = async (page: number, searchValue: string = ''): Promise<IPagination<IFranchiseeForSelect>> => {
    try {
      setSaveSearchValue(searchValue);
      if (savePage === page && paginationForSelect && (saveSearchValue === searchValue || !searchValue)) {
        return paginationForSelect;
      }

      if (searchValue && paginationForSelect) {
        const dataForSearch = paginationForSelect?.data.filter((value) => value.name.toUpperCase().includes(searchValue.toUpperCase()));
        const newPaginationForSearch = { ...paginationForSelect, data: dataForSearch?.length ? dataForSearch : [] };
        return newPaginationForSearch;
      }

      setSavePage(page);

      const pagination: IPagination<IFranchisee> = await reportsStore.getPagination(page);

      if (!pagination) {
        return defaultPagination;
      }

      const newPagination: IPagination<IFranchiseeForSelect> = transformToPaginationForSelect(pagination);

      const allUserBranches = await userStore.getAllBranches();

      const allFranchisees: IFranchiseeForSelect = {
        id: 0,
        name: localeStore.t('all_franchises'),
        data: allUserBranches,
      };

      newPagination.data.unshift(allFranchisees);

      // Preselect franchisee and branches
      if (!reportsStore.filters.franchisee) {
        selectFranchisee([{ ...allFranchisees, label: allFranchisees.name, value: allFranchisees.id }]);
      }

      setPaginationForSelect(newPagination);

      return newPagination;
    } catch (error: any) {
      logger.error({ error, place: 'ReportsFranchiseePicker getFranchisesPagination' });

      return defaultPagination;
    }
  };

  const options = useMemo(() => {
    return getFilterOptionsForPicker({ availableBranches: reportsStore.availableBranches, branches: reportsStore.filters.franchisee_branches });
  }, [reportsStore.availableBranches, reportsStore.filters.franchisee_branches, reportsStore.filters.branches]);

  return (
    <>
      <CustomLazyPicker
        className="mr-2"
        inputClassName="report-header__picker"
        onFetch={getFranchisesPagination}
        selectedOptions={reportsStore.filters.franchisee}
        onSelect={selectFranchisee}
        isSearchable
        emptyListText={localeStore.t('no_results_found')}
        small
        optionsDesc="franchise"
      />
      <Picker
        placeholder={localeStore.t('reports_all_branches_selected_placeholder')}
        emptyPlaceHolder={localeStore.t('report_select_branch_dropdown')}
        key="branches-picker"
        options={options}
        selectedOptions={reportsStore.filters.franchisee_branches}
        onSelect={selectBranches}
        hideResetIcon
        searchable
        isMulti
        className="orders-rating-header__branches-picker mr-2"
      />
    </>
  );
});

export default ReportsFranchiseePicker;
